import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { breadcrumbSchema, multiGraph } from "../components/Schema";
import { BRAND } from "../data/content";

const CONTENT = {
  "privacy-policy": {
    title: "Privacy policy",
    description: "How Spa Near Me Bhubaneswar collects, uses and protects the information you share.",
    sections: [
      {
        h: "Overview",
        p: [
          `${BRAND.name} ("we", "us") operates this website as a discovery and enquiry platform. This policy explains how we handle the limited information you share when you make an enquiry.`,
        ],
      },
      {
        h: "Information we collect",
        p: [
          "When you submit an enquiry form, we collect your name, phone number, chosen service, preferred date and time, number of guests, and your consent to be contacted.",
          "We also collect basic usage events (page views, button clicks) via analytics tools to improve the experience over time.",
        ],
      },
      {
        h: "How we use the information",
        p: [
          "The enquiry details are shared with our featured spa partner so they can confirm your appointment.",
          "We don't sell personal information.",
        ],
      },
      {
        h: "Data retention",
        p: [
          "Enquiries are stored securely and retained only for as long as needed to serve your request and comply with applicable laws.",
        ],
      },
      {
        h: "Your rights",
        p: [
          `You can request a copy or deletion of your data by writing to ${BRAND.email}.`,
        ],
      },
    ],
  },
  "terms": {
    title: "Terms & conditions",
    description: "The simple terms under which Spa Near Me Bhubaneswar operates.",
    sections: [
      {
        h: "About this website",
        p: [
          `${BRAND.name} is a discovery and enquiry platform. It is not an official Google directory, consumer authority or review authority.`,
          "Information displayed on this site is for general guidance. Business details are confirmed with our featured spa partner directly.",
        ],
      },
      {
        h: "Enquiries and bookings",
        p: [
          "Submitting an enquiry does not create a confirmed appointment. The partner spa will contact you to confirm availability, timings and pricing.",
          "Any contract for services is formed between you and the spa, not with this website.",
        ],
      },
      {
        h: "Accuracy",
        p: [
          "We work only with information our partner spa has confirmed. Where data is missing, we clearly mark it as pending rather than estimated.",
        ],
      },
      {
        h: "Changes",
        p: ["These terms may be updated from time to time. The latest version will always be available on this page."],
      },
    ],
  },
  "cancellation-policy": {
    title: "Cancellation policy",
    description: "How cancellations and rescheduling work for enquiries made via Spa Near Me Bhubaneswar.",
    sections: [
      {
        h: "Enquiry cancellation",
        p: [
          "You can cancel or reschedule an enquiry at any time before it is confirmed by the spa — simply reply to the WhatsApp conversation or email us.",
        ],
      },
      {
        h: "Confirmed appointments",
        p: [
          "Once an appointment is confirmed by the spa, the cancellation terms of the spa apply. These are shared at the time of confirmation.",
          "We encourage at least 4 hours' notice as a courtesy to the therapist and other guests.",
        ],
      },
      {
        h: "No-shows",
        p: [
          "Repeated no-shows may limit the ability of our featured partner to accept further enquiries from the same contact details.",
        ],
      },
    ],
  },
  "booking-policy": {
    title: "Booking policy",
    description: "How the enquiry-to-booking flow works at Spa Near Me Bhubaneswar.",
    sections: [
      {
        h: "How bookings work",
        p: [
          "You start with a short enquiry. The partner spa confirms availability, service details and pricing before any payment or final commitment.",
        ],
      },
      {
        h: "Arrival",
        p: [
          "Please arrive 10 minutes before your appointment to settle in and complete any brief consultation.",
          "Share any medical conditions, pregnancy, injuries or scent sensitivities with your therapist before the session begins.",
        ],
      },
      {
        h: "Payment",
        p: [
          "Payment is handled directly between you and the spa. We do not process payments on this website.",
        ],
      },
    ],
  },
  "disclaimer": {
    title: "Disclaimer",
    description: "Important context on how to read information on Spa Near Me Bhubaneswar.",
    sections: [
      {
        h: "General guidance only",
        p: [
          "The content on this website is intended as general guidance about spa and massage services in Bhubaneswar.",
          "It is not medical advice. If you have a health condition or are pregnant, consult a qualified medical professional before any treatment.",
        ],
      },
      {
        h: "Relationship with the featured partner",
        p: [
          `${BRAND.name} is a discovery platform. Our featured spa partner may compensate the platform for the enquiries we generate. We disclose this openly and continue to publish only verified information.`,
        ],
      },
      {
        h: "Third-party content",
        p: [
          "Any references to third-party brands or styles of massage (Swedish, Balinese, Thai, aromatherapy, etc.) describe common service formats and do not imply endorsement by, or affiliation with, any specific brand or lineage.",
        ],
      },
    ],
  },
};

export default function Legal({ page }) {
  const data = CONTENT[page];
  if (!data) return null;
  const crumbs = [{ label: "Home", href: "/" }, { label: data.title }];
  return (
    <>
      <Seo
        title={data.title}
        description={data.description}
        path={`/${page}/`}
        jsonLd={multiGraph(breadcrumbSchema(crumbs))}
      />
      <section className="content-hero section-pad narrow">
        <Breadcrumbs items={crumbs} />
        <p className="eyebrow">{BRAND.short}</p>
        <h1>{data.title}</h1>
        <p className="large-copy">{data.description}</p>
      </section>
      <section className="section-pad narrow legal-body">
        {data.sections.map((s, i) => (
          <div key={i} className="legal-section" data-testid={`legal-section-${i}`}>
            <h2>{s.h}</h2>
            {s.p.map((para, j) => <p key={j} className="large-copy">{para}</p>)}
          </div>
        ))}
      </section>
    </>
  );
}
